import { CustomField, CustomFieldType } from "@/Contracts/Settings";
import { Client } from "@/Contracts/Clients";
import { EWidgets } from "@organisms/ZForm/interface";
import { normalizeZipcode } from "@/V2Utils";
import moment from "moment";
import { CurrencyOptions } from "@/Contracts";

export interface AddressFieldConfig {
	id: string;
	key: string;
	label: string;
	isSystemField: boolean;
	isCustomField: boolean;
	fieldType: CustomFieldType;
	initialValue?: any;
	widget: EWidgets;
	widgetProps: any;
	normalize?: (value: any) => any;
	placeholder?: string;
}

/**
 * Display labels for system fields
 */
const SYSTEM_FIELD_LABELS: Record<string, string> = {
	address_line_1: "Street",
	address_line_2: "Street 2",
	city: "City",
	state: "State",
	country: "Country",
	pincode: "Zip Code",
	phone: "Phone"
};

/**
 * Get placeholder text for system fields
 * @param key - The field key (e.g., "address_line_1", "city")
 * @param addressType - Either "billing" or "shipping"
 * @returns The placeholder text (e.g., "Please enter shipping Street")
 */
const getSystemFieldPlaceholder = (
	key: string,
	addressType: "billing" | "shipping" = "billing"
): string => {
	const label = SYSTEM_FIELD_LABELS[key];
	if (!label)
		return `Please enter ${
			addressType === "shipping" ? "shipping " : ""
		}${key}`;

	// Fields that don't need " name" suffix: address_line_1, address_line_2, pincode
	const noSuffixFields = ["address_line_1", "address_line_2", "pincode"];
	const placeholderText = noSuffixFields.includes(key)
		? label
		: `${label} name`;

	return `Please enter ${
		addressType === "shipping" ? "shipping " : ""
	}${placeholderText}`;
};

/**
 * Get appropriate widget for custom field type
 */
const getWidgetForFieldType = (fieldType: CustomFieldType): EWidgets => {
	switch (fieldType) {
		case "text_field":
		case "system_field":
			return EWidgets.INPUT;
		case "dropdown_field":
		case "dropdown_field_with_key_value":
		case "multi_select_field":
		case "currency_field":
			return EWidgets.SELECT;
		case "number_field":
		case "decimal_field":
		case "amount_field":
			return EWidgets.INPUT;
		case "date_field":
			return EWidgets.DATEPICKER;
		default:
			return EWidgets.INPUT;
	}
};

/**
 * Get initial value for system field from client data
 */
const getSystemFieldInitialValue = (
	key: string,
	currentActiveClient: Client | null | undefined,
	addressType: "billing" | "shipping"
): any => {
	if (!currentActiveClient) return undefined;

	if (addressType === "billing") {
		return currentActiveClient.billing_address?.[key];
	} else {
		return currentActiveClient.ship_to_address?.[key];
	}
};

/**
 * Get initial value for custom field from client data
 */
const getCustomFieldInitialValue = (
	fieldId: string,
	currentActiveClient: Client | null | undefined,
	addressType: "billing" | "shipping"
): any => {
	if (!currentActiveClient) return undefined;

	const customFields =
		addressType === "billing"
			? currentActiveClient.billing_address?.custom_fields
			: currentActiveClient.ship_to_address?.custom_fields;

	const customField = customFields?.find(cf => cf.id === fieldId);

	// For multiselect fields, return the entire values array
	if (customField?.field_type === "multi_select_field") {
		return customField?.values || [];
	}

	const value = customField?.values?.[0];

	// For date fields, convert string to moment object
	if (value && customField?.field_type === "date_field") {
		const momentValue = moment(value);
		return momentValue;
	}

	return value || undefined;
};

/**
 * Transform ordered address fields from API into UI-ready field configurations
 */
export const transformAddressFieldsForUI = (
	orderedFields: CustomField[],
	currentActiveClient: Client | null | undefined,
	addressType: "billing" | "shipping" = "billing",
	required: boolean = false,
	isBulkUpdate: boolean = false
): AddressFieldConfig[] => {
	const transformedFields = orderedFields.map(field => {
		if (field.is_system_field && field.system_field_id) {
			// Handle system fields - use system_field_id directly as key
			const key = field.system_field_id;
			const label = field.display_name;
			const isPincode = key === "pincode";

			const initialValue = getSystemFieldInitialValue(
				key,
				currentActiveClient,
				addressType
			);

			const placeholder = getSystemFieldPlaceholder(key, addressType);

			return {
				id: field.id,
				key: addressType === "shipping" ? `ship_to_${key}` : key,
				label: addressType === "shipping" ? `Shipping ${label}` : label,
				isSystemField: true,
				isCustomField: false,
				fieldType: field.field_type,
				initialValue,
				widget: EWidgets.INPUT,
				normalize: isPincode ? normalizeZipcode : undefined,
				placeholder,
				widgetProps: {
					width: "full" as const,
					type: isPincode ? "text" : undefined,
					placeholder
				}
			};
		} else {
			// Handle custom fields
			const initialValue = getCustomFieldInitialValue(
				field.id,
				currentActiveClient,
				addressType
			);
			const widget = getWidgetForFieldType(field.field_type);

			let widgetProps: any = {
				width: "full" as const,
				placeholder: `Please enter ${
					addressType === "shipping" ? "shipping " : ""
				}${field.display_name}`
			};

			// Add specific props for different field types
			if (
				field.field_type === "dropdown_field" ||
				field.field_type === "dropdown_field_with_key_value" ||
				field.field_type === "multi_select_field"
			) {
				widgetProps = {
					...widgetProps,
					placeholder: `Please select ${
						addressType === "shipping" ? "shipping " : ""
					}${field.display_name}`,
					showSearch: true,
					allowClear: true,
					selectOptions:
						field.allowed_dropdown_values?.map(value => ({
							name: value,
							value: value
						})) || [],
					mode:
						field.field_type === "multi_select_field"
							? "multiple"
							: undefined
				};
			}

			// Add specific props for currency fields
			if (field.field_type === "currency_field") {
				widgetProps = {
					...widgetProps,
					placeholder: `Please select ${
						addressType === "shipping" ? "shipping " : ""
					}${field.display_name}`,
					showSearch: true,
					allowClear: true,
					selectOptions: CurrencyOptions.map(a => ({
						name: a.label,
						value: a.symbol
					}))
				};
			}

			return {
				id: field.id,
				key: `${addressType === "shipping" ? "ship_to_" : ""}custom_${
					field.id
				}`,
				label: `${addressType === "shipping" ? "Shipping " : ""}${
					field.display_name
				}`,
				isSystemField: false,
				isCustomField: true,
				fieldType: field.field_type,
				initialValue,
				widget,
				widgetProps,
				placeholder: widgetProps.placeholder
			};
		}
	});

	return transformedFields;
};

/**
 * Generate fallback field order when API is not available
 */
export const getFallbackFieldOrder = (): string[] => {
	return [
		"address_line_1",
		"address_line_2",
		"city",
		"state",
		"pincode",
		"country"
	];
};

/**
 * Convert fallback field keys to legacy format for existing getClientFormItem logic
 */
export const convertFallbackToLegacyFormat = (
	fallbackFields: string[],
	currentActiveClient: Client | null | undefined,
	required: boolean = false,
	isBulkUpdate: boolean = false,
	addressType: "billing" | "shipping" = "billing"
): AddressFieldConfig[] => {
	return fallbackFields.map(fieldKey => {
		const label = SYSTEM_FIELD_LABELS[fieldKey] || fieldKey;
		const isPincode = fieldKey === "pincode";
		const key =
			addressType === "shipping" ? `ship_to_${fieldKey}` : fieldKey;
		const initialValue = getSystemFieldInitialValue(
			fieldKey,
			currentActiveClient,
			addressType
		);

		const placeholder = getSystemFieldPlaceholder(fieldKey, addressType);

		return {
			id: `system_${fieldKey}`,
			key,
			label: addressType === "shipping" ? `Shipping ${label}` : label,
			isSystemField: true,
			isCustomField: false,
			fieldType: "system_field" as CustomFieldType,
			initialValue,
			widget: EWidgets.INPUT,
			normalize: isPincode ? normalizeZipcode : undefined,
			placeholder,
			widgetProps: {
				width: "full" as const,
				type: isPincode ? "text" : undefined,
				placeholder
			}
		};
	});
};

/**
 * Generate address field items for form rendering
 * Converts ordered address fields into form field configurations suitable for ZForm
 */
export const generateAddressFieldItems = (
	orderedFields: CustomField[],
	addressType: "billing" | "shipping" = "billing",
	initialValueFn: (field: CustomField) => any,
	isShippingAddress: boolean = false
) => {
	return orderedFields
		.map(field => {
			const initialValue = initialValueFn(field);

			// Handle system fields
			if (field.is_system_field) {
				// Backend now uses unprefixed IDs that match Address properties directly
				const key = field.system_field_id!;
				const label = field.display_name;
				const isPincode = key === "pincode";

				const placeholder = getSystemFieldPlaceholder(
					key,
					isShippingAddress ? "shipping" : "billing"
				);

				return {
					key,
					label: isShippingAddress ? `${label}` : label,
					hasFeedback: true,
					normalize: isPincode ? normalizeZipcode : undefined,
					initialValue,
					widget: EWidgets.INPUT,
					widgetProps: {
						width: "full",
						placeholder
					}
				};
			}

			// Handle custom fields
			const widget = getWidgetForFieldType(field.field_type);
			const key = `custom_${field.id}`;

			const widgetProps: any = {
				width: "full",
				placeholder: `Please enter ${
					isShippingAddress ? "shipping " : ""
				}${field.display_name}`
			};

			// Special handling for dropdown and multiselect fields
			if (
				(field.field_type === "dropdown_field" ||
					field.field_type === "multi_select_field") &&
				field.allowed_dropdown_values
			) {
				widgetProps.placeholder = `Please select ${
					isShippingAddress ? "shipping " : ""
				}${field.display_name}`;
				widgetProps.showSearch = true;
				widgetProps.allowClear = true;
				widgetProps.selectOptions = field.allowed_dropdown_values.map(
					(value: string) => ({
						name: value,
						value: value
					})
				);
				widgetProps.mode =
					field.field_type === "multi_select_field"
						? "multiple"
						: undefined;
			}

			// Special handling for currency fields
			if (field.field_type === "currency_field") {
				widgetProps.placeholder = `Please select ${
					isShippingAddress ? "shipping " : ""
				}${field.display_name}`;
				widgetProps.showSearch = true;
				widgetProps.allowClear = true;
				widgetProps.selectOptions = CurrencyOptions.map(a => ({
					name: a.label,
					value: a.symbol
				}));
			}

			return {
				key,
				label: field.display_name,
				hasFeedback: true,
				required: field.mandatory_field_details?.for_ui,
				message: `Please enter ${isShippingAddress ? "shipping " : ""}${
					field.display_name
				}`,
				initialValue,
				widget,
				widgetProps
			};
		})
		.filter(Boolean); // Remove null entries
};

/**
 * Extract address custom fields from form values for shipping address drawer
 */
export const extractAddressCustomFields = (
	formValues: any,
	orderedAddressFields: CustomField[]
): Record<string, any> => {
	const customFields: Record<string, any> = {};

	orderedAddressFields
		.filter(field => !field.is_system_field)
		.forEach(field => {
			const key = `custom_${field.id}`;
			if (formValues[key] !== undefined && formValues[key] !== "") {
				customFields[field.id] = formValues[key];
			}
		});

	return customFields;
};
